import Rollbar from "rollbar";
import { ILogObjMeta } from "tslog";
import { type Environment, getEnvironment } from "./environment";
import { Level, StructuredLog } from "./logger";

const ENABLED_ENVIRONMENTS: Array<Environment> = ["production", "staging"];

/**
 * Extends the Logger to also send items to Rollbar for
 * - logger.error
 * - logger.fatal
 *
 * @example
 * ```ts
 * const logger = new Logger({
 *   application: "Offer Experience",
 *   runtime: "web",
 *   transports: [rollbarTransport(rollbar)],
 * });
 * logger.error("I will go to Rollbar!");
 * ```
 */
const rollbarTransport =
  (rollbar: Rollbar): ((logObj: StructuredLog & ILogObjMeta) => void) =>
  (logObj) => {
    // eslint-disable-next-line no-underscore-dangle
    switch (logObj._meta.logLevelName as Level) {
      case "FATAL":
        rollbar.critical(logObj.message, logObj.metadata, logObj.error);
        break;
      case "ERROR":
        rollbar.error(logObj.message, logObj.metadata, logObj.error);
        break;
      default:
        break;
    }
  };

/**
 * Initializes Rollbar with default values.
 * @example
 * ```ts
 * const rollbar = createRollbar("accessToken", "Offer Create UI");
 * ```
 */
const createRollbar = (
  accessToken: string,
  context: string,
  overrides: Partial<Rollbar.Configuration> = {},
): Rollbar =>
  new Rollbar({
    accessToken,
    captureUncaught: true,
    captureUnhandledRejections: true,
    enabled: ENABLED_ENVIRONMENTS.includes(getEnvironment()),
    payload: {
      client: {
        javascript: {
          guess_uncaught_frames: true,
          source_map_enabled: true,
        },
      },
      context,
      environment: getEnvironment(),
    },
    ...overrides,
  });

export { createRollbar, rollbarTransport };
